import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, ChevronDown, ExternalLink, Maximize2, Pause, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { meQueryOptions, useMe } from '~/api/auth';
import { useServerClock } from '~/api/serverClock';
import {
  type Occurrence,
  type OccurrenceStep,
  meActiveQueryOptions,
  taskQueryOptions,
  useUpdateSteps,
} from '~/api/work';
import { CompleteDialog, PauseDialog } from '~/features/work/dialogs';
import { liveSeconds, overBudgetBy } from '~/features/work/live';
import { desktop } from '~/lib/desktop';
import { formatClock, formatMinutes } from '~/lib/format';
import { notify } from '~/lib/notify';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Skeleton } from '~/ui/Skeleton';
import { Hint } from '~/ui/Hint';

/**
 * The desktop app's mini timer: a small window that stays on top of other apps
 * while a task timer runs and the 11Deeds window is not in front. It shows the
 * running duty, its time against the budget (with a bar along the bottom), and
 * Pause and Complete with their dialogs. The duty's title opens what it asks,
 * its description and checklist, in the same window. The window grows for the
 * dialogs and the details (`setMiniExpanded`). It lives outside the app shell,
 * since it has no room for a sidebar; signed out it shows nothing, and the
 * desktop app hides it.
 */
export const Route = createFileRoute('/mini-timer')({
  beforeLoad: async ({ context }) => {
    // Signed out: the page renders nothing; the main window asks for sign-in.
    await context.queryClient.ensureQueryData(meQueryOptions).catch(() => undefined);
  },
  component: MiniTimerPage,
});

function MiniTimerPage() {
  const me = useQuery(meQueryOptions);
  useEffect(() => {
    document.title = '11Deeds timer';
  }, []);
  if (!me.data) return null;
  return <MiniTimer />;
}

const noDrag = '[-webkit-app-region:no-drag]';

function MiniTimer() {
  const bridge = desktop();
  const me = useMe();
  const now = useServerClock(1000);
  const active = useQuery({ ...meActiveQueryOptions, refetchInterval: 60_000 });
  const [pausing, setPausing] = useState(false);
  const [completing, setCompleting] = useState(false);
  // The run whose details are open; another run (or none) closes them.
  const [detailsFor, setDetailsFor] = useState<string | null>(null);

  // The main window says when the running timer changes (started, paused, done elsewhere).
  const { refetch } = active;
  useEffect(() => bridge?.onActiveRun?.(() => void refetch()), [bridge, refetch]);

  const o = active.data?.active?.occurrence ?? null;
  const details = o !== null && detailsFor === o.id;
  const open = pausing || completing || details;
  useEffect(() => {
    bridge?.setMiniExpanded?.(open);
  }, [bridge, open]);

  // Escape folds the details away; an open dialog takes its own Escape first.
  useEffect(() => {
    if (!details || pausing || completing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDetailsFor(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [details, pausing, completing]);

  if (!active.data) return null;
  const serverTime = active.data.serverTime;
  const seconds = o ? liveSeconds(o, serverTime, now) : 0;
  const overBy = o ? overBudgetBy(seconds, o.budgetMinutes, me.workspace.budgetTolerancePct) : 0;
  const budgetSeconds = o?.budgetMinutes ? o.budgetMinutes * 60 : null;
  const pct = budgetSeconds ? Math.floor((seconds / budgetSeconds) * 100) : 0;

  // The run itself on the Tasks page; the details fold away so the window can step aside.
  const openInApp = () => {
    setDetailsFor(null);
    bridge?.showMain?.(o ? `/tasks?run=${encodeURIComponent(o.id)}` : '/tasks');
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-paper text-ink">
      <div className="relative flex h-16 shrink-0 items-center gap-3 border-b border-line bg-accent-deep px-3 text-white [-webkit-app-region:drag]">
        <span
          aria-hidden
          className={`size-2.5 shrink-0 rounded-full ${o?.status === 'active' ? 'animate-pulse bg-done' : 'bg-white/40'}`}
        />
        <div className="min-w-0 flex-1">
          {o ? (
            <Hint label={details ? 'Hide the task details' : 'Show the task details'} side="bottom">
              <button
                type="button"
                onClick={() => setDetailsFor(details ? null : o.id)}
                aria-expanded={details}
                className={`${noDrag} flex max-w-full items-center gap-1 rounded text-left text-xs opacity-80 hover:underline hover:opacity-100`}
              >
                <span className="truncate">{o.taskTitle}</span>
                <ChevronDown
                  size={12}
                  aria-hidden
                  className={`shrink-0 transition-transform ${details ? 'rotate-180' : ''}`}
                />
              </button>
            </Hint>
          ) : (
            <p className="truncate text-xs opacity-80">No timer running</p>
          )}
          <p className="flex items-baseline gap-2">
            <span className="font-display text-xl leading-tight font-semibold tabular">
              {formatClock(seconds)}
            </span>
            {o && o.budgetMinutes !== null && (
              <span className={`truncate text-[11px] ${overBy > 0 ? 'text-warn' : 'opacity-70'}`}>
                {overBy > 0
                  ? `${formatMinutes(overBy / 60)} over`
                  : `of ${formatMinutes(o.budgetMinutes)} · ${pct}%`}
              </span>
            )}
          </p>
        </div>
        {o && (
          <>
            <Hint label="Pause" side="left">
              <button
                type="button"
                onClick={() => setPausing(true)}
                aria-label="Pause"
                className={`${noDrag} grid size-9 place-items-center rounded-full bg-white text-accent-deep hover:scale-105`}
              >
                <Pause size={16} aria-hidden />
              </button>
            </Hint>
            <Hint label="Complete" side="left">
              <button
                type="button"
                onClick={() => setCompleting(true)}
                aria-label="Complete"
                className={`${noDrag} grid size-9 place-items-center rounded-full bg-late text-white hover:scale-105`}
              >
                <Check size={16} aria-hidden />
              </button>
            </Hint>
          </>
        )}
        <div className={`${noDrag} flex flex-col`}>
          <Hint label="Open in Tasks" side="left">
            <button
              type="button"
              onClick={openInApp}
              aria-label="Open in Tasks"
              className="grid size-6 place-items-center rounded opacity-70 hover:bg-white/10 hover:opacity-100"
            >
              <Maximize2 size={13} aria-hidden />
            </button>
          </Hint>
          <Hint label="Hide until the next timer" side="left">
            <button
              type="button"
              onClick={() => bridge?.hideMini?.()}
              aria-label="Hide until the next timer"
              className="grid size-6 place-items-center rounded opacity-70 hover:bg-white/10 hover:opacity-100"
            >
              <X size={13} aria-hidden />
            </button>
          </Hint>
        </div>
        {budgetSeconds !== null && (
          <TimeBar
            seconds={seconds}
            budgetSeconds={budgetSeconds}
            tone={overBy > 0 ? 'late' : seconds > budgetSeconds ? 'warn' : 'done'}
          />
        )}
      </div>
      {details && o && <Details occurrence={o} onOpenInApp={openInApp} />}
      {o && (
        <>
          <PauseDialog occurrence={o} open={pausing} onClose={() => setPausing(false)} />
          <CompleteDialog
            occurrence={o}
            serverTime={serverTime}
            open={completing}
            onClose={() => setCompleting(false)}
          />
        </>
      )}
    </div>
  );
}

/** Time spent against the budget, along the bar's bottom edge: full at the budget. */
function TimeBar({
  seconds,
  budgetSeconds,
  tone,
}: {
  seconds: number;
  budgetSeconds: number;
  tone: 'done' | 'warn' | 'late';
}) {
  const pct = Math.max(0, Math.min(100, (seconds / budgetSeconds) * 100));
  const fill = { done: 'bg-done', warn: 'bg-warn', late: 'bg-late' }[tone];
  return (
    <div
      role="progressbar"
      aria-label="Time spent against the budget"
      aria-valuenow={Math.round(seconds / 60)}
      aria-valuemin={0}
      aria-valuemax={Math.round(budgetSeconds / 60)}
      className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-white/15"
    >
      <div
        className={`h-full ${fill} transition-[width] duration-700 ease-[var(--ease-settle)]`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** What the running duty asks: its title in full, the description, and this run's checklist. */
function Details({
  occurrence: o,
  onOpenInApp,
}: {
  occurrence: Occurrence;
  onOpenInApp: () => void;
}) {
  const task = useQuery(taskQueryOptions(o.taskId));
  const steps = useUpdateSteps();
  const list = o.steps ?? [];
  const done = list.filter((s) => s.done).length;

  const toggleStep = async (step: OccurrenceStep) => {
    const next = list.map((s) => (s.id === step.id ? { ...s, done: !s.done } : s));
    try {
      await steps.mutateAsync({ id: o.id, steps: next });
    } catch (err) {
      notify.error('Could not tick that.', err);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
        <h3 className="text-base leading-snug">
          {o.taskRef && (
            <span className="mr-2 font-mono text-xs font-semibold text-ink-3">{o.taskRef}</span>
          )}
          {o.taskTitle}
        </h3>

        <section aria-label="Description">
          <h4 className="mb-1.5 font-mono text-[11px] tracking-[0.06em] text-ink-3 uppercase">
            Description
          </h4>
          {task.isPending ? (
            <Skeleton lines={3} />
          ) : task.isError ? (
            <ErrorNotice error={task.error} onRetry={() => task.refetch()} />
          ) : task.data.sop ? (
            <div className="rounded-control border border-line bg-paper-2/40 px-3 py-2 text-sm leading-6 whitespace-pre-wrap text-ink">
              {task.data.sop}
            </div>
          ) : (
            <p className="text-sm text-ink-3">No description written.</p>
          )}
        </section>

        <section aria-label="Checklist">
          <h4 className="mb-1.5 flex items-baseline justify-between font-mono text-[11px] tracking-[0.06em] text-ink-3 uppercase">
            Checklist
            {list.length > 0 && (
              <span className="tabular">
                {done} of {list.length} done
              </span>
            )}
          </h4>
          {list.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {list.map((s) => (
                <li key={s.id}>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={s.done}
                      disabled={steps.isPending}
                      onChange={() => void toggleStep(s)}
                      className="mt-1 size-4 accent-[var(--accent)]"
                    />
                    <span className={s.done ? 'text-ink-3 line-through' : ''}>{s.text}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-3">No steps on this task.</p>
          )}
        </section>
      </div>
      <div className="shrink-0 border-t border-line px-4 py-2.5">
        <button
          type="button"
          onClick={onOpenInApp}
          className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
        >
          Open in Tasks <ExternalLink size={13} aria-hidden />
        </button>
      </div>
    </div>
  );
}
